import { useState, useContext, useEffect } from "react";
import HeaderBar from "../components/HeaderBar";
import VehicleLayoutWrapper from "../components/VehicleLayoutWrapper";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaChevronDown, FaChevronUp, FaPrint } from "react-icons/fa";

const RenewalAlertsDashboard = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  
  // State for container expansion
  const [expandedContainer, setExpandedContainer] = useState(null);
  
  // Sample data for the containers
  const [containers] = useState([
    { id: 1, title: "OVN VEHICLE RENEWAL ALERTS", color: "blue" },
    { id: 2, title: "PERIODIC ENTRY ALERTS", color: "green" },
    { id: 3, title: "FUEL AND LUBRICANT COST", color: "orange" }
  ]);
  
  // Sample data for OVN Vehicle Renewal Alerts table
  const [ovnAlerts] = useState([
    { id: 1, store: "Main Store", vehicleName: "Toyota Camry", vehicleCode: "TC001", vehicleNumber: "KA01AB1234", vehicleType: "Sedan", insurance: "2023-12-31", fc: "2023-11-30", roadTax: "2024-01-15", permit: "2023-10-20", localPermit: "2023-09-15", trPermit: "2024-02-28" },
    { id: 2, store: "West Store", vehicleName: "Honda City", vehicleCode: "HC002", vehicleNumber: "KA02CD5678", vehicleType: "Sedan", insurance: "2023-11-30", fc: "2023-10-31", roadTax: "2023-12-20", permit: "2023-09-25", localPermit: "2023-08-20", trPermit: "2024-01-31" },
    { id: 3, store: "North Store", vehicleName: "Ford EcoSport", vehicleCode: "FE003", vehicleNumber: "KA03EF9012", vehicleType: "SUV", insurance: "2024-01-31", fc: "2023-12-31", roadTax: "2024-02-15", permit: "2023-11-30", localPermit: "2023-10-25", trPermit: "2024-03-31" }
  ]);
  
  // Sample data for Periodic Entry Alerts table
  const [periodicAlerts] = useState([
    { id: 1, branch: "Main Branch", vehicleName: "Toyota Camry", vehicleCode: "TC001", subAssemblyName: "Engine Oil", description: "Oil Change", oilGrade: "10W-40", lastChangedDate: "2023-06-15", lastChangedHrKm: "5000 km", serviceLimit: "10000 km", nextServiceHrKm: "15000 km", currentCumulativeHrKm: "12000 km", remainingHr: "3000 km", alert: "Due Soon" },
    { id: 2, branch: "West Branch", vehicleName: "Honda City", vehicleCode: "HC002", subAssemblyName: "Air Filter", description: "Filter Replacement", oilGrade: "N/A", lastChangedDate: "2023-05-20", lastChangedHrKm: "8000 km", serviceLimit: "15000 km", nextServiceHrKm: "23000 km", currentCumulativeHrKm: "21000 km", remainingHr: "2000 km", alert: "Due Soon" },
    { id: 3, branch: "North Branch", vehicleName: "Ford EcoSport", vehicleCode: "FE003", subAssemblyName: "Brake Pads", description: "Brake Service", oilGrade: "N/A", lastChangedDate: "2023-04-10", lastChangedHrKm: "15000 km", serviceLimit: "30000 km", nextServiceHrKm: "45000 km", currentCumulativeHrKm: "42000 km", remainingHr: "3000 km", alert: "Due Soon" }
  ]);
  
  // Sample data for Fuel and Lubricant Cost table
  const [fuelCostData] = useState([
    { id: 1, vehicleName: "Toyota Camry", vehicleCode: "TC001", fuelType: "Petrol", fuelCost: "₹5,200", lubricantCost: "₹1,500", total: "₹6,700", month: "October 2023" },
    { id: 2, vehicleName: "Honda City", vehicleCode: "HC002", fuelType: "Petrol", fuelCost: "₹4,800", lubricantCost: "₹1,200", total: "₹6,000", month: "October 2023" },
    { id: 3, vehicleName: "Ford EcoSport", vehicleCode: "FE003", fuelType: "Diesel", fuelCost: "₹6,500", lubricantCost: "₹1,800", total: "₹8,300", month: "October 2023" }
  ]);

  const toggleContainer = (containerId) => {
    if (expandedContainer === containerId) {
      setExpandedContainer(null);
    } else {
      setExpandedContainer(containerId);
    }
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Renewal Alerts Dashboard</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}>
                Renewal Alerts Dashboard
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className={`rounded-xl p-6 mb-6 border ${
              isDark 
                ? "bg-gray-800 border-gray-700" 
                : "bg-gray-50 border-gray-200"
            }`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Branch Select */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select className={inputClass}>
                    <option>Select</option>
                    <option>Main Branch</option>
                    <option>West Branch</option>
                    <option>North Branch</option>
                  </select>
                </div>
                
                {/* Store Select */}
                <div>
                  <label className={labelClass}>Store</label>
                  <select className={inputClass}>
                    <option>Select</option>
                    <option>Main Store</option>
                    <option>West Store</option>
                    <option>North Store</option>
                  </select>
                </div>
                
                {/* Vehicle Number */}
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Enter vehicle number"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>
                <button
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search Records
                </button>
                <button
               onClick={() => navigate('/vehicle/DashBoardAlertPrint')}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          {/* Containers Section */}
          <div className="space-y-6">
            {containers.map(container => (
              <div key={container.id} className={`rounded-xl overflow-hidden border ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}>
                {/* Container Header */}
                <div 
                  className={`p-4 flex justify-between items-center cursor-pointer ${
                    container.color === "blue" ? "bg-blue-600" : 
                    container.color === "green" ? "bg-green-600" : 
                    "bg-orange-600"
                  } text-white`}
                  onClick={() => toggleContainer(container.id)}
                >
                  <h2 className="text-lg font-semibold">{container.title}</h2>
                  {expandedContainer === container.id ? (
                    <FaChevronUp className="w-5 h-5" />
                  ) : (
                    <FaChevronDown className="w-5 h-5" />
                  )}
                </div>
                
                {/* Container Content (Table) */}
                {expandedContainer === container.id && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${isDark ? "bg-gray-800" : "bg-gray-100"} text-left`}>
                        {container.id === 1 && (
                          <tr>
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold">Store</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Name</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Code</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Number</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Type</th>
                            <th className="px-4 py-3 font-semibold">Insurance</th>
                            <th className="px-4 py-3 font-semibold">FC</th>
                            <th className="px-4 py-3 font-semibold">Road Tax</th>
                            <th className="px-4 py-3 font-semibold">Permit</th>
                            <th className="px-4 py-3 font-semibold">Local Permit</th>
                            <th className="px-4 py-3 font-semibold">TR Permit</th>
                            <th className="px-4 py-3 font-semibold">View</th>
                          </tr>
                        )}
                        {container.id === 2 && (
                          <tr>
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold">Branch</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Name</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Code</th>
                            <th className="px-4 py-3 font-semibold">Sub Assembly Name</th>
                            <th className="px-4 py-3 font-semibold">Description</th>
                            <th className="px-4 py-3 font-semibold">Oil Grade/Part No</th>
                            <th className="px-4 py-3 font-semibold">Last Changed Date</th>
                            <th className="px-4 py-3 font-semibold">Last Changed HR/KM</th>
                            <th className="px-4 py-3 font-semibold">Service Limit</th>
                            <th className="px-4 py-3 font-semibold">Next Service HR/KM</th>
                            <th className="px-4 py-3 font-semibold">Current Cumulative HR/KM</th>
                            <th className="px-4 py-3 font-semibold">Remaining HR</th>
                            <th className="px-4 py-3 font-semibold">Alert</th>
                          </tr>
                        )}
                        {container.id === 3 && (
                          <tr>
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Name</th>
                            <th className="px-4 py-3 font-semibold">Vehicle Code</th>
                            <th className="px-4 py-3 font-semibold">Fuel Type</th>
                            <th className="px-4 py-3 font-semibold">Fuel Cost</th>
                            <th className="px-4 py-3 font-semibold">Lubricant Cost</th>
                            <th className="px-4 py-3 font-semibold">Total</th>
                            <th className="px-4 py-3 font-semibold">Month</th>
                          </tr>
                        )}
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {container.id === 1 && ovnAlerts.map((alert, idx) => (
                          <tr key={alert.id} className={isDark ? "bg-gray-900 hover:bg-gray-800" : "bg-white hover:bg-gray-50"}>
                            <td className="px-4 py-3">{idx + 1}</td>
                            <td className="px-4 py-3">{alert.store}</td>
                            <td className="px-4 py-3">{alert.vehicleName}</td>
                            <td className="px-4 py-3">{alert.vehicleCode}</td>
                            <td className="px-4 py-3">{alert.vehicleNumber}</td>
                            <td className="px-4 py-3">{alert.vehicleType}</td>
                            <td className="px-4 py-3">{alert.insurance}</td>
                            <td className="px-4 py-3">{alert.fc}</td>
                            <td className="px-4 py-3">{alert.roadTax}</td>
                            <td className="px-4 py-3">{alert.permit}</td>
                            <td className="px-4 py-3">{alert.localPermit}</td>
                            <td className="px-4 py-3">{alert.trPermit}</td>
                            <td className="px-4 py-3">
                              <button className="text-blue-600 dark:text-blue-400 hover:underline">
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                        {container.id === 2 && periodicAlerts.map((alert, idx) => (
                          <tr key={alert.id} className={isDark ? "bg-gray-900 hover:bg-gray-800" : "bg-white hover:bg-gray-50"}>
                            <td className="px-4 py-3">{idx + 1}</td>
                            <td className="px-4 py-3">{alert.branch}</td>
                            <td className="px-4 py-3">{alert.vehicleName}</td>
                            <td className="px-4 py-3">{alert.vehicleCode}</td>
                            <td className="px-4 py-3">{alert.subAssemblyName}</td>
                            <td className="px-4 py-3">{alert.description}</td>
                            <td className="px-4 py-3">{alert.oilGrade}</td>
                            <td className="px-4 py-3">{alert.lastChangedDate}</td>
                            <td className="px-4 py-3">{alert.lastChangedHrKm}</td>
                            <td className="px-4 py-3">{alert.serviceLimit}</td>
                            <td className="px-4 py-3">{alert.nextServiceHrKm}</td>
                            <td className="px-4 py-3">{alert.currentCumulativeHrKm}</td>
                            <td className="px-4 py-3">{alert.remainingHr}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {alert.alert}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {container.id === 3 && fuelCostData.map((data, idx) => (
                          <tr key={data.id} className={isDark ? "bg-gray-900 hover:bg-gray-800" : "bg-white hover:bg-gray-50"}>
                            <td className="px-4 py-3">{idx + 1}</td>
                            <td className="px-4 py-3">{data.vehicleName}</td>
                            <td className="px-4 py-3">{data.vehicleCode}</td>
                            <td className="px-4 py-3">{data.fuelType}</td>
                            <td className="px-4 py-3">{data.fuelCost}</td>
                            <td className="px-4 py-3">{data.lubricantCost}</td>
                            <td className="px-4 py-3">{data.total}</td>
                            <td className="px-4 py-3">{data.month}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default RenewalAlertsDashboard;